import { useEffect, useState } from "react";
import type * as Y from "yjs";
import {
  type DomainMaps,
  type Sheet,
  type SheetId,
  listSheetsInOrder,
} from "@electro-planner/domain";
import { DEFAULT_SHEET_ID } from "../constants.ts";

export type SheetNavigation = {
  sheets: Sheet[];
  activeSheetId: SheetId;
  setActiveSheetId: (id: SheetId) => void;
  activeIndex: number;
};

/**
 * Observerar sheets- och sheetOrder-strukturerna och håller activeSheetId
 * giltigt: om det aktiva arket försvinner (deleteSheet från någon håll)
 * faller vi tillbaka till första tillgängliga sheet.
 *
 * Tolererar tomt utgångsläge: useYjsDoc seedar default-sheet efter att
 * IndexedDB har synkat, så under första tick kan sheets vara tomma.
 * Vi använder då DEFAULT_SHEET_ID som platshållare och observern korrigerar
 * när det riktiga arket landar.
 */
export function useSheetNavigation(
  _doc: Y.Doc,
  maps: DomainMaps,
): SheetNavigation {
  const [sheets, setSheets] = useState<Sheet[]>(() => listSheetsInOrder(maps));
  const [activeSheetId, setActiveSheetIdState] = useState<SheetId>(
    () => sheets[0]?.id ?? DEFAULT_SHEET_ID,
  );

  useEffect(() => {
    const refresh = () => {
      const next = listSheetsInOrder(maps);
      setSheets(next);
      setActiveSheetIdState((current) => {
        if (next.some((s) => s.id === current)) return current;
        const first = next[0];
        return first ? first.id : current;
      });
    };

    refresh();
    maps.sheets.observe(refresh);
    maps.sheetOrder.observe(refresh);
    return () => {
      maps.sheets.unobserve(refresh);
      maps.sheetOrder.unobserve(refresh);
    };
  }, [maps]);

  const activeIndex = sheets.findIndex((s) => s.id === activeSheetId);

  return {
    sheets,
    activeSheetId,
    setActiveSheetId: setActiveSheetIdState,
    activeIndex: activeIndex < 0 ? 0 : activeIndex,
  };
}
